/**
 * Token Estimation Utility
 * 
 * Provides functions to estimate token counts for AI requests and manage token budgets.
 * Uses approximation methods to estimate tokens without requiring external APIs.
 * 
 * @author AI Assistant
 * @version 1.0.0
 */

// Token budget constants
export const MAX_TOKENS_SIMPLE = 1000;
export const MAX_TOKENS_MEDIUM = 2000;
export const MAX_TOKENS_COMPLEX = 3000;
export const EMERGENCY_LIMIT = 4000;

// Token estimation constants
const CHARS_PER_TOKEN = 4; // Base approximation: 1 token ≈ 4 characters
const JSON_OVERHEAD_MULTIPLIER = 1.2; // JSON structure adds ~20% overhead
const CODE_BLOCK_MULTIPLIER = 1.5; // Code blocks are more token-dense
const SPECIAL_CHAR_MULTIPLIER = 1.1; // Special characters add slight overhead

/**
 * Basic token estimation using character count approximation
 * 
 * @param {string} text - The text to estimate tokens for
 * @returns {number} Estimated token count
 * @throws {Error} If text is not a string
 * 
 * @example
 * const tokens = estimateTokens("Hello world!");
 * console.log(tokens); // ~3
 */
export const estimateTokens = (text) => {
  try {
    if (typeof text !== 'string') {
      throw new Error('Input must be a string');
    }
    
    if (!text || text.length === 0) {
      return 0;
    }
    
    // Basic estimation: divide character count by average chars per token
    const baseTokens = Math.ceil(text.length / CHARS_PER_TOKEN);
    
    // Apply multipliers based on content characteristics
    let multiplier = 1;
    
    // Check for JSON structure
    if (isJsonLike(text)) {
      multiplier *= JSON_OVERHEAD_MULTIPLIER;
    }
    
    // Check for code blocks
    if (hasCodeBlocks(text)) {
      multiplier *= CODE_BLOCK_MULTIPLIER;
    }
    
    // Check for high density of special characters
    if (hasHighSpecialCharDensity(text)) {
      multiplier *= SPECIAL_CHAR_MULTIPLIER;
    }
    
    return Math.ceil(baseTokens * multiplier);
    
  } catch (error) {
    console.error('Error estimating tokens:', error);
    throw new Error(`Token estimation failed: ${error.message}`);
  }
};

/**
 * Comprehensive token estimation for complete AI prompts
 * 
 * @param {string} prompt - The main prompt text
 * @param {string|Object} context - Context information (string or object)
 * @param {Array} history - Conversation history array
 * @returns {Object} Detailed token breakdown
 * @throws {Error} If required parameters are invalid
 * 
 * @example
 * const breakdown = estimatePromptTokens(
 *   "What does this control require?",
 *   { control: "CIS-1.1.1", description: "..." },
 *   [{ question: "...", answer: "..." }]
 * );
 * console.log(breakdown.total); // Total estimated tokens
 */
export const estimatePromptTokens = (prompt, context = '', history = []) => {
  try {
    // Validate inputs
    if (typeof prompt !== 'string') {
      throw new Error('Prompt must be a string');
    }
    
    if (!Array.isArray(history)) {
      throw new Error('History must be an array');
    }
    
    // Convert context to string if it's an object
    const contextString = typeof context === 'object' 
      ? JSON.stringify(context, null, 2)
      : String(context || '');
    
    // Estimate tokens for each component
    const promptTokens = estimateTokens(prompt);
    const contextTokens = estimateTokens(contextString);
    
    // Estimate history tokens
    const historyTokens = history.reduce((total, exchange) => {
      const questionTokens = estimateTokens(exchange.question || '');
      const answerTokens = estimateTokens(exchange.answer || '');
      return total + questionTokens + answerTokens;
    }, 0);
    
    // System message overhead (typical AI system prompts)
    const systemOverhead = 50; // Estimated system message tokens
    
    // Calculate total with overhead
    const subtotal = promptTokens + contextTokens + historyTokens + systemOverhead;
    const total = Math.ceil(subtotal * 1.1); // 10% buffer for formatting overhead
    
    return {
      prompt: promptTokens,
      context: contextTokens,
      history: historyTokens,
      systemOverhead,
      subtotal,
      total,
      breakdown: {
        promptPercentage: Math.round((promptTokens / total) * 100),
        contextPercentage: Math.round((contextTokens / total) * 100),
        historyPercentage: Math.round((historyTokens / total) * 100)
      }
    };
    
  } catch (error) {
    console.error('Error estimating prompt tokens:', error);
    throw new Error(`Prompt token estimation failed: ${error.message}`);
  }
};

/**
 * Validates if token count is within specified budget
 * 
 * @param {number} totalTokens - Total estimated tokens
 * @param {number} maxBudget - Maximum allowed tokens
 * @returns {Object} Validation result with status and recommendations
 * @throws {Error} If parameters are invalid
 * 
 * @example
 * const validation = validateTokenBudget(2500, MAX_TOKENS_MEDIUM);
 * if (!validation.isValid) {
 *   console.log(validation.message);
 *   console.log(validation.suggestions);
 * }
 */
export const validateTokenBudget = (totalTokens, maxBudget) => {
  try {
    // Validate inputs
    if (typeof totalTokens !== 'number' || totalTokens < 0) {
      throw new Error('Total tokens must be a non-negative number');
    }
    
    if (typeof maxBudget !== 'number' || maxBudget <= 0) {
      throw new Error('Max budget must be a positive number');
    }
    
    const isValid = totalTokens <= maxBudget;
    const utilizationPercentage = Math.round((totalTokens / maxBudget) * 100);
    const excessTokens = Math.max(0, totalTokens - maxBudget);
    
    // Determine severity level
    let severity = 'low';
    if (utilizationPercentage > 90) {
      severity = 'high';
    } else if (utilizationPercentage > 75) {
      severity = 'medium';
    }
    
    // Generate suggestions based on budget status
    const suggestions = generateBudgetSuggestions(totalTokens, maxBudget, severity);
    
    return {
      isValid,
      totalTokens,
      maxBudget,
      utilizationPercentage,
      excessTokens,
      severity,
      message: isValid 
        ? `Token budget OK (${utilizationPercentage}% of ${maxBudget} tokens)`
        : `Token budget exceeded by ${excessTokens} tokens (${utilizationPercentage}% of ${maxBudget})`,
      suggestions
    };
    
  } catch (error) {
    console.error('Error validating token budget:', error);
    throw new Error(`Token budget validation failed: ${error.message}`);
  }
};

/**
 * Helper function to detect JSON-like structure in text
 * 
 * @private
 * @param {string} text - Text to analyze
 * @returns {boolean} True if text appears to be JSON-like
 */
const isJsonLike = (text) => {
  const jsonIndicators = ['{', '}', '[', ']', ':', '"'];
  const indicatorCount = jsonIndicators.reduce((count, indicator) => {
    return count + (text.split(indicator).length - 1);
  }, 0);
  
  // Consider JSON-like if more than 10% of characters are JSON indicators
  return (indicatorCount / text.length) > 0.1;
};

/**
 * Helper function to detect code blocks in text
 * 
 * @private
 * @param {string} text - Text to analyze
 * @returns {boolean} True if text contains code blocks
 */
const hasCodeBlocks = (text) => {
  const codeIndicators = [
    '```', // Markdown code blocks
    '`', // Inline code
    'function ', // JavaScript functions
    'const ', // Variable declarations
    'import ', // Import statements
    'export ', // Export statements
    '  {', // Indented objects
    '  }', // Indented closures
  ];
  
  return codeIndicators.some(indicator => text.includes(indicator));
};

/**
 * Helper function to detect high density of special characters
 * 
 * @private
 * @param {string} text - Text to analyze
 * @returns {boolean} True if text has high special character density
 */
const hasHighSpecialCharDensity = (text) => {
  const specialChars = text.match(/[^a-zA-Z0-9\s]/g) || [];
  const specialCharRatio = specialChars.length / text.length;
  
  // Consider high density if more than 20% are special characters
  return specialCharRatio > 0.2;
};

/**
 * Helper function to generate budget optimization suggestions
 * 
 * @private
 * @param {number} totalTokens - Current token count
 * @param {number} maxBudget - Maximum budget
 * @param {string} severity - Severity level (low/medium/high)
 * @returns {Array<string>} Array of suggestion strings
 */
const generateBudgetSuggestions = (totalTokens, maxBudget, severity) => {
  const suggestions = [];
  
  if (totalTokens <= maxBudget) {
    if (severity === 'medium') {
      suggestions.push('Consider reducing context size for faster responses');
    } else if (severity === 'high') {
      suggestions.push('Close to token limit - consider optimizing prompt');
      suggestions.push('Remove unnecessary conversation history');
    }
  } else {
    // Over budget - provide optimization suggestions
    suggestions.push('Reduce conversation history to last 2-3 exchanges');
    suggestions.push('Summarize or trim context information');
    suggestions.push('Break complex questions into smaller parts');
    suggestions.push('Remove redundant or duplicate information');
    
    if (totalTokens > EMERGENCY_LIMIT) {
      suggestions.push('⚠️ CRITICAL: Request exceeds emergency limit - major optimization required');
    }
  }
  
  return suggestions;
};

/**
 * Utility function to get appropriate token budget based on request complexity
 * 
 * @param {string} complexity - Complexity level ('simple', 'medium', 'complex')
 * @returns {number} Appropriate token budget
 * @throws {Error} If complexity level is invalid
 * 
 * @example
 * const budget = getTokenBudgetForComplexity('medium');
 * console.log(budget); // 2000
 */
export const getTokenBudgetForComplexity = (complexity) => {
  const budgets = {
    simple: MAX_TOKENS_SIMPLE,
    medium: MAX_TOKENS_MEDIUM,
    complex: MAX_TOKENS_COMPLEX,
    emergency: EMERGENCY_LIMIT
  };
  
  if (!budgets.hasOwnProperty(complexity)) {
    throw new Error(`Invalid complexity level: ${complexity}. Must be one of: ${Object.keys(budgets).join(', ')}`);
  }
  
  return budgets[complexity];
};

/**
 * Debug utility to log detailed token breakdown
 * 
 * @param {Object} tokenBreakdown - Result from estimatePromptTokens
 * @param {string} label - Optional label for the log
 * 
 * @example
 * const breakdown = estimatePromptTokens(prompt, context, history);
 * logTokenBreakdown(breakdown, 'User Query Analysis');
 */
export const logTokenBreakdown = (tokenBreakdown, label = 'Token Analysis') => {
  if (process.env.NODE_ENV === 'development') {
    console.group(`🔍 ${label}`);
    console.log(`Total Tokens: ${tokenBreakdown.total}`);
    console.log(`├─ Prompt: ${tokenBreakdown.prompt} (${tokenBreakdown.breakdown.promptPercentage}%)`);
    console.log(`├─ Context: ${tokenBreakdown.context} (${tokenBreakdown.breakdown.contextPercentage}%)`);
    console.log(`├─ History: ${tokenBreakdown.history} (${tokenBreakdown.breakdown.historyPercentage}%)`);
    console.log(`└─ System Overhead: ${tokenBreakdown.systemOverhead}`);
    console.groupEnd();
  }
};

// Export all constants and functions
export default {
  // Constants
  MAX_TOKENS_SIMPLE,
  MAX_TOKENS_MEDIUM,
  MAX_TOKENS_COMPLEX,
  EMERGENCY_LIMIT,
  
  // Core functions
  estimateTokens,
  estimatePromptTokens,
  validateTokenBudget,
  
  // Utility functions
  getTokenBudgetForComplexity,
  logTokenBreakdown
};
